import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { BoilerCascade, DayTimetable, HeatingCircuit, IntervalTimetable, WeekTimetable, } from 'z3k-config';
import { MaterialObjectList, } from '../../material-utils/material-object-list';
import { boiler_cascades_and_hc_options_selector, consumer_heating_circuits_options_selector, wired_radio_options_selector, } from 'z3k-parser/lib/z3k-selectors';
import { useBox, usePartial } from 'shared/hooks/utils';
import { removeTemperatureSetting } from '../utils';
import { HeatingZone } from './component';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { c2k } from 'utils';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
export const HeatingZoneList = memo(function HeatingZoneList(props) {
    const { value: heating_zones, validation_result, validation_warnings_result, onChange, mode, is_boiler_mode, is_boiler_list, } = props;
    const { service_view, addIdToBlacklist } = useContext(Z3KSettings);
    const { config, replace_config: onChangeConfig } = useContext(Z3KSettingsEditor);
    const configBox = useBox(config);
    const heatingModeBox = useBox(mode);
    const wired_radio_options = wired_radio_options_selector(config);
    const day_timetables = DayTimetable.get.all(config);
    const week_timetables = WeekTimetable.get.all(config);
    const interval_timetables = IntervalTimetable.get.all(config);
    const boiler_heating_circuits_cascades_options = boiler_cascades_and_hc_options_selector(config);
    const heating_circuits_options = consumer_heating_circuits_options_selector(config);
    const hc_options = useMemo(() => is_boiler_mode || mode?.force_boiler_mode
        ? boiler_heating_circuits_cascades_options
        : heating_circuits_options, [
        is_boiler_mode,
        mode?.force_boiler_mode,
        boiler_heating_circuits_cascades_options,
        heating_circuits_options,
    ]);
    const _getHZName = useCallback((_config, hz) => {
        const hc = HeatingCircuit.get.one(_config, 
        // prettier-ignore
        ({ id }) => id === hz.heating_circuit);
        const boiler_cascade = BoilerCascade.get.one(_config, 
        // prettier-ignore
        ({ id }) => id === hz.heating_circuit);
        return hc ? hc.name : boiler_cascade ? boiler_cascade.name : '';
    }, []);
    const getHZName = usePartial(_getHZName, config);
    const options = useMemo(() => ({
        hc_options,
        mode,
        wired_radio_options,
        day_timetables,
        week_timetables,
        interval_timetables,
    }), [
        hc_options,
        mode,
        wired_radio_options,
        day_timetables,
        week_timetables,
        interval_timetables,
    ]);
    const onAdd = useCallback(() => onChangeConfig(HeatingMode.get.applySetterById(configBox.current, heatingModeBox.current.id, {
        heating_zones: [
            ...heatingModeBox.current.heating_zones,
            {
                heating_circuit: null,
                temperature_setting: c2k(20),
                adjusting_sensor: null,
                pza: null,
            },
        ],
    })), [onChangeConfig]);
    const onDelete = useCallback((hz, hzs) => {
        onChangeConfig(HeatingMode.get.applySetterById(removeTemperatureSetting(configBox.current, hz.temperature_setting, addIdToBlacklist), heatingModeBox.current.id, {
            heating_zones: hzs,
        }));
    }, [onChangeConfig]);
    const modify_hzs = useMemo(() => heating_zones.map((hz, index) => ({ ...hz, id: index })), [heating_zones]);
    //В результате неправильного добавления id в modify_hzs,
    // они стали попадать в базу и появляться странные зависимости
    const _onChange = useCallback((hzs) => onChange(hzs.map((hz) => {
        const { id, ...rest } = hz;
        return rest;
    })), [onChange]);
    const show = useCallback(() => service_view, [service_view]);
    return (_jsx(ErrorBoundary, { children: _jsx(MaterialObjectList, { name: is_boiler_list
                ? 'Настройка котловых контуров'
                : 'Настройки отопительных контуров', help_id: "heating_modes_zone_settings", object: HeatingZone, nameGetter: getHZName, value: modify_hzs, object_props: options, validation_result: validation_result, validation_warnings_result: validation_warnings_result, onChange: _onChange, onAddClick: onAdd, onDelete: onDelete, showTrashButton: show, hideAddButton: !service_view }) }));
});
